// Teach-Back — the learner coaches a struggling student.
//
// Pedagogy: the protégé effect (learning by teaching) + misconception
// diagnosis + self-explanation. The learner never writes prose a heuristic
// scores; they JUDGE authored material, so every action is a selection.
// Beats (CREATE-COMPOSITION-HANDOVER.md §9 / §9.1):
//   1. red-pen the artifact (strike damage phrases; over-striking the
//      loadBearing line is punished)
//   2. diagnose the belief underneath (select the misconception)
//   3. compose coaching from ordered fragments (opening + inclusion pick the branch)
//   4. the student responds (aha / partial / backfire / dependency; one adapt beat)
//   5. the residue check (strike the belief's fingerprint in the rewrite)
//   6. authored close + expert contrast + show-the-record
//
// One composite capture record on the terminal action:
//   { struck, overStruck, diagnosisId, diagnosisCorrect, coachingPath,
//     outcome, adapted, residueCaught }

import type { TeachBackSlideData, Theme } from '../../slideTypes';
import type { ActivityResponsePayload } from '../../../../../types';
import { feedbackStyle } from '../../shared/SlideShared';
import { ExperienceClose } from '../../shared/ExperienceClose';
import { useTeachBack } from './useTeachBack';
import { LamplightTeachBack } from './LamplightTeachBack';

interface Props {
  data: TeachBackSlideData;
  theme: Theme;
  isEditing: boolean;
  onUpdate: (data: TeachBackSlideData) => void;
  onActivityResponse?: (response: ActivityResponsePayload) => void;
  onComplete?: () => void;
  savedResponse?: Record<string, unknown> | null;
}

export function TeachBackSlide({
  data,
  theme,
  isEditing,
  onUpdate,
  onActivityResponse,
  onComplete,
  savedResponse,
}: Props) {
  if (isEditing) return <BasicEditor data={data} theme={theme} onUpdate={onUpdate} />;
  // Bespoke "Lamplight" Tier-A skin — same shared machine (useTeachBack), desk-and-lamp presentation.
  if (theme.id === 'lamplight') {
    return (
      <LamplightTeachBack
        data={data}
        theme={theme}
        onActivityResponse={onActivityResponse}
        onComplete={onComplete}
        savedResponse={savedResponse}
      />
    );
  }
  return (
    <Student
      data={data}
      theme={theme}
      onActivityResponse={onActivityResponse}
      onComplete={onComplete}
      savedResponse={savedResponse}
    />
  );
}

function Student({
  data,
  theme,
  onActivityResponse,
  onComplete,
  savedResponse,
}: {
  data: TeachBackSlideData;
  theme: Theme;
  onActivityResponse?: (response: ActivityResponsePayload) => void;
  onComplete?: () => void;
  savedResponse?: Record<string, unknown> | null;
}) {
  const ink = theme.colors.text;
  const muted = theme.colors.textMuted;
  const accent = theme.colors.accent || theme.colors.primary;
  const primary = theme.colors.primary;
  const surface = theme.colors.surface;
  const headingFont = theme.fonts.heading;
  const bodyFont = theme.fonts.body;
  const monoFont = theme.fonts.code || theme.fonts.body;

  // The machine is shared with the Lamplight skin so a flagship can't drift.
  const {
    phase,
    struck,
    toggleStrike,
    penDown,
    damageStruck,
    damageTotal,
    overStruck,
    diagnosisId,
    chooseDiagnosis,
    diagnosisCorrect,
    chosenDiagnosis,
    composed,
    addFragment,
    removeFragment,
    sayIt,
    outcome,
    studentReply,
    coachNote,
    chooseAdapt,
    residueStruck,
    strikeResidue,
    residueCaught,
    approveRevision,
    adapted,
  } = useTeachBack(data, { onActivityResponse, onComplete, savedResponse });

  // ── Shared styles ──────────────────────────────────────────────────────────
  const label: React.CSSProperties = {
    fontFamily: monoFont,
    fontSize: 11,
    letterSpacing: '0.18em',
    textTransform: 'uppercase',
    color: accent,
  };
  const hint: React.CSSProperties = { fontFamily: bodyFont, fontStyle: 'italic', fontSize: 14, color: muted };

  return (
    <div
      className="h-full w-full overflow-y-auto"
      style={{ background: theme.colors.background, color: ink, fontFamily: bodyFont }}
    >
      <div style={{ maxWidth: 820, margin: '0 auto', padding: '48px 24px 96px' }}>
        {/* header */}
        <header style={{ marginBottom: 22 }}>
          <div style={{ ...label, marginBottom: 10 }}>Coaching · {data.student.role}</div>
          <h1 style={{ fontFamily: headingFont, fontSize: 'clamp(1.6rem, 4vw, 2.4rem)', lineHeight: 1.1, margin: 0 }}>
            {data.title}
          </h1>
          {data.subtitle && <p style={{ color: muted, marginTop: 8, fontSize: 15 }}>{data.subtitle}</p>}
          {data.instruction && <p style={{ ...hint, marginTop: 12 }}>{data.instruction}</p>}
        </header>

        {/* the student's opening voice */}
        <div
          style={{
            background: surface,
            borderRadius: 12,
            padding: '14px 16px',
            marginBottom: 22,
            borderLeft: `3px solid ${accent}`,
          }}
        >
          <div style={{ ...label, fontSize: 10, marginBottom: 4 }}>
            {data.student.name} · {data.student.role}
          </div>
          <p style={{ fontFamily: bodyFont, fontSize: 15, margin: 0, color: ink }}>“{data.student.voiceOpening}”</p>
        </div>

        {/* ===== BEAT 1 · RED PEN ===== */}
        <Artifact
          artifact={data.artifact}
          struck={struck}
          locked={phase !== 'redpen'}
          onToggle={toggleStrike}
          theme={theme}
        />
        {phase === 'redpen' && (
          <div style={{ marginTop: 12 }}>
            <PrimaryButton disabled={struck.size === 0} onClick={penDown} theme={theme}>
              Pen down — that&apos;s what I&apos;d strike
            </PrimaryButton>
          </div>
        )}
        {phase !== 'redpen' && (
          <div
            style={{
              marginTop: 10,
              padding: '10px 14px',
              borderRadius: 8,
              fontSize: 13.5,
              background: `${(damageStruck >= damageTotal && !overStruck ? theme.colors.primary : accent)}18`,
              border: `1px solid ${accent}55`,
              color: ink,
            }}
          >
            You caught <b>{damageStruck}</b> of <b>{damageTotal}</b> damage phrases.
            {overStruck && ' You also struck a line the reader actually needs — a red pen can cut the good out too.'}
          </div>
        )}

        {/* ===== BEAT 2 · DIAGNOSE ===== */}
        {(phase === 'diagnose' || (phase !== 'redpen' && diagnosisId)) && (
          <section style={{ marginTop: 28 }}>
            <div style={{ ...label, marginBottom: 6 }}>Beat 2 · The belief underneath</div>
            <p style={{ ...hint, marginBottom: 12 }}>
              Not just where it&apos;s wrong — what does the student believe that made every one of those phrases feel
              right?
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {data.diagnosisOptions.map((o) => {
                const picked = diagnosisId === o.id;
                const locked = phase !== 'diagnose';
                const fb = feedbackStyle(theme, o.correct ? 'correct' : 'incorrect');
                return (
                  <button
                    key={o.id}
                    type="button"
                    disabled={locked}
                    onClick={() => chooseDiagnosis(o.id)}
                    style={{
                      textAlign: 'left',
                      background: picked ? `${(o.correct ? theme.colors.primary : accent)}18` : surface,
                      border: `1px solid ${picked ? (o.correct ? fb.border : accent) : `${muted}44`}`,
                      color: ink,
                      borderRadius: 10,
                      padding: '12px 15px',
                      fontFamily: bodyFont,
                      fontSize: 14,
                      cursor: locked ? 'default' : 'pointer',
                      opacity: locked && !picked ? 0.5 : 1,
                    }}
                  >
                    {o.text}
                  </button>
                );
              })}
            </div>
            {diagnosisId && chosenDiagnosis && (
              <div
                style={{
                  marginTop: 10,
                  padding: '10px 14px',
                  borderRadius: 8,
                  fontSize: 13.5,
                  background: `${(diagnosisCorrect ? theme.colors.primary : accent)}18`,
                  border: `1px solid ${accent}55`,
                }}
              >
                {chosenDiagnosis.feedback}
              </div>
            )}
          </section>
        )}

        {/* ===== BEAT 3 · COMPOSE ===== */}
        {(phase === 'compose' || phase === 'reply' || phase === 'adapt' || phase === 'residue' || phase === 'done') &&
          diagnosisId && (
            <section style={{ marginTop: 28 }}>
              <div style={{ ...label, marginBottom: 6 }}>Beat 3 · Your coaching — in the order you&apos;d say it</div>
              <p style={{ ...hint, marginBottom: 12 }}>
                What you lead with changes how the student hears everything after it. {data.maxCoaching} max.
              </p>
              <Composer
                fragments={data.coachingFragments}
                composed={composed}
                locked={phase !== 'compose'}
                max={data.maxCoaching}
                onAdd={addFragment}
                onRemove={removeFragment}
                theme={theme}
              />
              {phase === 'compose' && (
                <div style={{ marginTop: 12 }}>
                  <PrimaryButton disabled={composed.length === 0} onClick={sayIt} theme={theme}>
                    Say it to {data.student.name}
                  </PrimaryButton>
                </div>
              )}
            </section>
          )}

        {/* student reply + coach note (after Say it) */}
        {(phase === 'reply' || phase === 'adapt' || phase === 'residue' || phase === 'done') && studentReply && (
          <div style={{ marginTop: 18 }}>
            <div style={{ background: surface, borderRadius: '12px 12px 12px 3px', padding: '12px 16px', borderLeft: `3px solid ${muted}66` }}>
              <div style={{ ...label, fontSize: 10, marginBottom: 4 }}>{data.student.name}</div>
              <p style={{ fontSize: 14.5, margin: 0 }}>{studentReply}</p>
            </div>
            {coachNote && (
              <div style={{ marginTop: 8, fontSize: 13.5, color: muted, fontStyle: 'italic' }}>{coachNote}</div>
            )}
          </div>
        )}

        {/* ===== ADAPT BEAT ===== */}
        {phase === 'adapt' && data.adaptBeat && (
          <section style={{ marginTop: 20 }}>
            <div style={{ ...label, marginBottom: 8 }}>{data.adaptBeat.prompt}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {data.adaptBeat.options.map((o) => (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => chooseAdapt(o.id)}
                  style={{
                    textAlign: 'left',
                    background: surface,
                    border: `1px solid ${muted}44`,
                    color: ink,
                    borderRadius: 10,
                    padding: '12px 15px',
                    fontFamily: bodyFont,
                    fontSize: 14,
                    cursor: 'pointer',
                  }}
                >
                  {o.text}
                </button>
              ))}
            </div>
          </section>
        )}

        {/* ===== BEAT 4 · RESIDUE ===== */}
        {(phase === 'residue' || phase === 'done') && (
          <section style={{ marginTop: 28 }}>
            <div style={{ ...label, marginBottom: 6 }}>Beat 5 · The rewrite is back — final judgement</div>
            <p style={{ ...hint, marginBottom: 12 }}>
              It&apos;s better. But beliefs leave fingerprints. Approve it — or strike what shouldn&apos;t have survived.
            </p>
            <RevisionView
              revision={data.revision}
              struck={residueStruck}
              locked={phase !== 'residue'}
              onStrike={strikeResidue}
              theme={theme}
            />
            {phase === 'residue' && (
              <div style={{ marginTop: 12 }}>
                <PrimaryButton disabled={false} onClick={approveRevision} theme={theme}>
                  {residueStruck ? 'Strike it & approve' : 'Approve — send it'}
                </PrimaryButton>
              </div>
            )}
          </section>
        )}

        {/* ===== CLOSE ===== */}
        {phase === 'done' && (
          <section style={{ marginTop: 30 }}>
            <div
              style={{
                background: `${accent}12`,
                borderLeft: `3px solid ${accent}`,
                borderRadius: 8,
                padding: '14px 16px',
                marginBottom: 16,
              }}
            >
              <div style={{ ...label, fontSize: 10, marginBottom: 6 }}>The expert teacher&apos;s move</div>
              <p style={{ fontSize: 14, margin: 0, color: ink }}>{data.expertNote}</p>
            </div>

            <ExperienceClose
              conclusion={data.conclusion}
              theme={theme}
              outcome={outcome ?? 'partial'}
              values={{
                damageFound: String(damageStruck),
                damageTotal: String(damageTotal),
                diagnosis: diagnosisCorrect ? 'named the belief' : 'read it as a surface slip',
              }}
              fallback="You coached a real belief today — that is the whole skill."
            />

            {/* SHOW-THE-RECORD (§10 law) */}
            <div style={{ marginTop: 16, background: surface, border: `1px solid ${muted}33`, borderRadius: 10, overflow: 'hidden' }}>
              <div
                style={{
                  ...label,
                  fontSize: 10,
                  padding: '8px 14px',
                  background: `${primary}10`,
                  borderBottom: `1px solid ${muted}33`,
                }}
              >
                Your record · what Kula keeps
              </div>
              <div style={{ padding: '12px 16px', fontFamily: monoFont, fontSize: 12, color: muted, lineHeight: 1.9 }}>
                Red pen: <b style={{ color: ink }}>{damageStruck}/{damageTotal}</b> damage caught
                {overStruck && <span style={{ color: theme.colors.textMuted }}> · 1 load-bearing line struck</span>} ·
                {' '}Diagnosis: <b style={{ color: ink }}>{diagnosisCorrect ? 'correct' : 'missed'}</b> ·
                {' '}Outcome: <b style={{ color: ink }}>{outcome}</b>
                {adapted && ' (adapted)'} ·{' '}
                Residue: <b style={{ color: ink }}>{residueCaught ? 'caught' : 'shipped'}</b>
              </div>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

// ── Artifact (beat 1) ─────────────────────────────────────────────────────────
function Artifact({
  artifact,
  struck,
  locked,
  onToggle,
  theme,
}: {
  artifact: TeachBackSlideData['artifact'];
  struck: Set<string>;
  locked: boolean;
  onToggle: (id: string) => void;
  theme: Theme;
}) {
  const monoFont = theme.fonts.code || theme.fonts.body;
  const bodyFont = theme.fonts.body;
  return (
    <div style={{ position: 'relative' }}>
      <div style={{ position: 'absolute', top: -12, right: 12, zIndex: 2 }}>
        <span
          style={{
            fontFamily: monoFont,
            fontSize: 11,
            letterSpacing: '0.06em',
            color: '#fff',
            background: theme.colors.accent || theme.colors.primary,
            padding: '3px 10px',
            borderRadius: 12,
          }}
        >
          {struck.size} struck
        </span>
      </div>
      <div
        style={{
          background: '#f6f2ea',
          color: '#26221a',
          borderRadius: 10,
          padding: '20px 22px',
          fontFamily: bodyFont,
          fontSize: 15,
          lineHeight: 1.75,
          boxShadow: '0 12px 30px rgba(0,0,0,0.25)',
        }}
      >
        {artifact.header && artifact.header.length > 0 && (
          <div
            style={{
              fontFamily: monoFont,
              fontSize: 11.5,
              color: '#7a7364',
              borderBottom: '1px solid #ddd6c7',
              paddingBottom: 9,
              marginBottom: 13,
            }}
          >
            {artifact.header.map((h, i) => (
              <span key={i} style={{ marginRight: 14 }}>
                {h.label}: <b style={{ color: '#3a3428' }}>{h.value}</b>
              </span>
            ))}
          </div>
        )}
        <p style={{ margin: 0 }}>
          {artifact.fragments.map((f) => {
            if (!f.strikeable) return <span key={f.id}>{f.text} </span>;
            const isStruck = struck.has(f.id);
            return (
              <span
                key={f.id}
                role="button"
                tabIndex={locked ? -1 : 0}
                onClick={() => !locked && onToggle(f.id)}
                onKeyDown={(e) => {
                  if (!locked && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    onToggle(f.id);
                  }
                }}
                style={{
                  cursor: locked ? 'default' : 'pointer',
                  borderRadius: 3,
                  padding: '0 2px',
                  textDecoration: isStruck ? 'line-through' : 'none',
                  textDecorationColor: '#c0392b',
                  textDecorationThickness: isStruck ? '2px' : undefined,
                  background: isStruck ? 'rgba(192,57,43,0.12)' : 'transparent',
                  color: isStruck ? '#8f2c20' : '#26221a',
                }}
              >
                {f.text}
              </span>
            );
          })}
        </p>
      </div>
    </div>
  );
}

// ── Composer (beat 3) ─────────────────────────────────────────────────────────
function Composer({
  fragments,
  composed,
  locked,
  max,
  onAdd,
  onRemove,
  theme,
}: {
  fragments: TeachBackSlideData['coachingFragments'];
  composed: string[];
  locked: boolean;
  max: number;
  onAdd: (id: string) => void;
  onRemove: (id: string) => void;
  theme: Theme;
}) {
  const ink = theme.colors.text;
  const muted = theme.colors.textMuted;
  const accent = theme.colors.accent || theme.colors.primary;
  const surface = theme.colors.surface;
  const monoFont = theme.fonts.code || theme.fonts.body;
  const available = fragments.filter((f) => !composed.includes(f.id));
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }} className="teachback-composer">
      <div style={{ background: `${muted}11`, border: `1px solid ${muted}33`, borderRadius: 12, padding: 12 }}>
        <div style={{ fontFamily: monoFont, fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: muted, marginBottom: 9 }}>
          Fragments
        </div>
        {available.map((f) => (
          <button
            key={f.id}
            type="button"
            disabled={locked || composed.length >= max}
            onClick={() => onAdd(f.id)}
            style={{
              display: 'block',
              width: '100%',
              textAlign: 'left',
              background: surface,
              border: `1px solid ${muted}44`,
              color: ink,
              borderRadius: 8,
              padding: '9px 12px',
              fontFamily: theme.fonts.body,
              fontSize: 13,
              cursor: locked ? 'default' : 'pointer',
              marginBottom: 7,
              opacity: locked ? 0.6 : 1,
            }}
          >
            {f.text}
          </button>
        ))}
        {available.length === 0 && <div style={{ fontSize: 12, color: muted, fontStyle: 'italic' }}>All placed.</div>}
      </div>
      <div style={{ background: `${accent}0d`, border: `1.5px dashed ${accent}66`, borderRadius: 12, padding: 12 }}>
        <div style={{ fontFamily: monoFont, fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: muted, marginBottom: 9 }}>
          Your coaching · tap to remove
        </div>
        {composed.length === 0 && (
          <div style={{ fontSize: 12.5, color: muted, fontStyle: 'italic' }}>Nothing yet — what&apos;s your opening move?</div>
        )}
        {composed.map((id, i) => {
          const f = fragments.find((x) => x.id === id);
          if (!f) return null;
          return (
            <button
              key={id}
              type="button"
              disabled={locked}
              onClick={() => onRemove(id)}
              style={{
                position: 'relative',
                display: 'block',
                width: '100%',
                textAlign: 'left',
                background: `${accent}1a`,
                border: `1px solid ${accent}66`,
                color: ink,
                borderRadius: 8,
                padding: '9px 12px 9px 24px',
                fontFamily: theme.fonts.body,
                fontSize: 13,
                cursor: locked ? 'default' : 'pointer',
                marginBottom: 7,
              }}
            >
              <span
                style={{
                  position: 'absolute',
                  left: -9,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: 18,
                  height: 18,
                  borderRadius: '50%',
                  background: accent,
                  color: '#fff',
                  fontFamily: monoFont,
                  fontSize: 10,
                  display: 'grid',
                  placeItems: 'center',
                }}
              >
                {i + 1}
              </span>
              {f.text}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Revision view (beat 4 residue) ────────────────────────────────────────────
function RevisionView({
  revision,
  struck,
  locked,
  onStrike,
  theme,
}: {
  revision: TeachBackSlideData['revision'];
  struck: boolean;
  locked: boolean;
  onStrike: () => void;
  theme: Theme;
}) {
  const monoFont = theme.fonts.code || theme.fonts.body;
  const bodyFont = theme.fonts.body;
  return (
    <div
      style={{
        background: '#f6f2ea',
        color: '#26221a',
        borderRadius: 10,
        padding: '20px 22px',
        fontFamily: bodyFont,
        fontSize: 15,
        lineHeight: 1.75,
        boxShadow: '0 12px 30px rgba(0,0,0,0.25)',
      }}
    >
      {revision.header && revision.header.length > 0 && (
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 11.5,
            color: '#7a7364',
            borderBottom: '1px solid #ddd6c7',
            paddingBottom: 9,
            marginBottom: 13,
          }}
        >
          {revision.header.map((h, i) => (
            <span key={i} style={{ marginRight: 14 }}>
              {h.label}: <b style={{ color: '#3a3428' }}>{h.value}</b>
            </span>
          ))}
        </div>
      )}
      <p style={{ margin: 0 }}>
        {revision.fragments.map((f) => {
          if (!f.strikeable) return <span key={f.id}>{f.text} </span>;
          return (
            <span
              key={f.id}
              role="button"
              tabIndex={locked ? -1 : 0}
              onClick={() => !locked && onStrike()}
              onKeyDown={(e) => {
                if (!locked && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault();
                  onStrike();
                }
              }}
              title={locked ? undefined : 'Strike if this shouldn’t have survived'}
              style={{
                cursor: locked ? 'default' : 'pointer',
                borderRadius: 3,
                padding: '0 2px',
                textDecoration: struck ? 'line-through' : 'none',
                textDecorationColor: '#c0392b',
                textDecorationThickness: struck ? '2px' : undefined,
                background: struck ? 'rgba(192,57,43,0.12)' : 'transparent',
                color: struck ? '#8f2c20' : '#26221a',
                outline: struck ? '1.5px dashed #c0392b' : 'none',
                outlineOffset: 1,
              }}
            >
              {f.text}
            </span>
          );
        })}
      </p>
    </div>
  );
}

// ── Primary button ────────────────────────────────────────────────────────────
function PrimaryButton({
  children,
  disabled,
  onClick,
  theme,
}: {
  children: React.ReactNode;
  disabled: boolean;
  onClick: () => void;
  theme: Theme;
}) {
  const accent = theme.colors.accent || theme.colors.primary;
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      style={{
        background: accent,
        color: '#fff',
        border: 'none',
        borderRadius: 10,
        padding: '12px 26px',
        fontFamily: theme.fonts.body,
        fontWeight: 600,
        fontSize: 14,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.4 : 1,
      }}
    >
      {children}
    </button>
  );
}

// ── Minimal editor (structural edits are AI-only for scenario types) ──────────
function BasicEditor({
  data,
  theme,
  onUpdate,
}: {
  data: TeachBackSlideData;
  theme: Theme;
  onUpdate: (data: TeachBackSlideData) => void;
}) {
  const muted = theme.colors.textMuted;
  const field: React.CSSProperties = {
    width: '100%',
    background: theme.colors.surface,
    border: `1px solid ${muted}44`,
    borderRadius: 8,
    color: theme.colors.text,
    padding: '10px 12px',
    fontFamily: theme.fonts.body,
    fontSize: 14,
    marginBottom: 12,
  };
  return (
    <div style={{ padding: 24, maxWidth: 720, margin: '0 auto', color: theme.colors.text }}>
      <label style={{ fontSize: 12, color: muted, display: 'block', marginBottom: 4 }}>Title</label>
      <input
        style={field}
        value={data.title}
        onChange={(e) => onUpdate({ ...data, title: e.target.value })}
      />
      <label style={{ fontSize: 12, color: muted, display: 'block', marginBottom: 4 }}>Subtitle</label>
      <input
        style={field}
        value={data.subtitle}
        onChange={(e) => onUpdate({ ...data, subtitle: e.target.value })}
      />
      <label style={{ fontSize: 12, color: muted, display: 'block', marginBottom: 4 }}>Instruction</label>
      <textarea
        style={{ ...field, minHeight: 70 }}
        value={data.instruction}
        onChange={(e) => onUpdate({ ...data, instruction: e.target.value })}
      />
      <p style={{ fontSize: 12.5, color: muted, fontStyle: 'italic' }}>
        The student, artifact, diagnosis options, coaching fragments and branches are authored by Kula Create — edit them
        via “Edit with AI”.
      </p>
    </div>
  );
}
